<template>
  <div
    v-sc-non-empty="props.breadcrumb"
    v-sc-non-empty-string="props.title"
    :class="{
      'post-breadcrumbs px-7 pb-5 pt-[42px] text-10px font-medium capitalize leading-5 text-www-color-neutral-medium-dark md:px-0 md:py-[22px] md:text-xs lg:py-30px lg:text-15px': true,
    }"
    data-testid="BreadCrumb"
  >
    <div :class="{ 'fluid-container mx-auto': true }">
      <nav aria-label="breadcrumb">
        <ol
          :class="{
            'breadcrumb flex': true,
          }"
        >
          <li
            v-for="(crumb, index) in props.breadcrumb"
            :key="index"
            :class="{
              'breadcrumb-item': true,
            }"
          >
            <PTCLink
              :class="{
                'whitespace-nowrap text-www-color-primary-light': true,
              }"
              :to="getCrumbPath(crumb.to)"
              >{{ crumb.text.replace(/-/g, ' ') }}</PTCLink
            >
            <span
              :class="{
                'mx-1': true,
              }"
              >&gt;</span
            >
          </li>
          <li
            :class="{
              'overflow-hidden text-ellipsis whitespace-nowrap text-www-color-neutral-medium md:overflow-visible': true,
            }"
          >
            <span>{{ props.title }}</span>
          </li>
        </ol>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import PTCLink from '../PTCLink/PTCLink.vue';
import { serializeJsonLd } from '~/utils/json-ld-utils';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  breadcrumb: {
    type: Array<{
      text: string;
      to: string;
    }>,
    required: true,
  },
});

const route = useRoute();
const instanceUid = getCurrentInstance()?.uid;
const langPrefix = '/' + route.path.split('/')[1];
const baseUrl = process.env.NUXT_BASE_URL || 'https://www.onshape.com';
const sanitizedRouteKey = computed(() => {
  const key = route.path
    .replace(/[^a-zA-Z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return key || 'root';
});
const breadcrumbScriptKey = computed(
  () => `structured-data-breadcrumb-${sanitizedRouteKey.value}-${instanceUid ?? 'unknown'}`
);

const currentPagePath = computed(() => {
  const normalizedPath = route.path.endsWith('/') ? route.path : `${route.path}/`;
  return normalizedPath;
});

function getCrumbPath(path: string): string {
  const normalizedCrumbPath = path.startsWith('/') ? path : `/${path}`;
  return `${langPrefix}${normalizedCrumbPath}/`.replace(/\/+/g, '/');
}

function buildAbsoluteUrl(path: string): string {
  return new URL(path, baseUrl).toString();
}

const breadcrumbJsonLd = computed(() => {
  const homePath = `${langPrefix}/`;
  const listItems = [
    {
      name: 'Home',
      path: homePath,
    },
    ...props.breadcrumb.map((crumb) => ({
      name: crumb.text.replace(/-/g, ' '),
      path: getCrumbPath(crumb.to),
    })),
    {
      name: props.title,
      path: currentPagePath.value,
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: listItems.map((item, index) => {
      const isLast = index === listItems.length - 1;
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        ...(isLast ? {} : { item: buildAbsoluteUrl(item.path) }),
      };
    }),
  };
});

useHead(() => ({
  script: [
    {
      key: breadcrumbScriptKey.value,
      type: 'application/ld+json',
      innerHTML: serializeJsonLd(breadcrumbJsonLd.value),
    },
  ],
}));
</script>
